'use client'

import React, { useState, useEffect, useRef } from 'react'
import { Button, message, Upload } from 'antd'
import styled, { keyframes } from 'styled-components'
import axios from 'axios'
/* BiometricCheck is intentionally not used here — the design has no separate
   identity check screen. Proctoring happens silently during the live
   interview via the frame-upload loop in interview.js. */
import Timer from 'components/Timer'
import { useSelector } from 'react-redux'
import { useNavigate } from 'react-router-dom'
import Interview from './interview'
import { parseResume, fetchConfigInfo } from './aiInterviewAPI'
import {
  readInviteScopedJwt,
  isAiInterviewCompleted,
  markAiInterviewCompleted,
  clearAiInterviewCompleted,
} from './aiInterviewInviteAPI'
import { handleResumeFile as runResumeUpload } from './resumeUpload'
import { primeTtsAudio } from './ttsAudio'
import { initUploadQueueResumption } from './sessionUploadQueue'

const VALID_RESUME_POLICIES = new Set(['mandatory', 'optional', 'not_required'])

/* ============================================================================
 * Design tokens — single source of truth, mirrors dev.pluginlive.com/design.
 * Cobalt #494FDF, 6px radii (max), Satoshi, 4px grid.
 * ========================================================================= */
const T = {
  bgPage: '#FFFFFF',
  bgSurface: '#F5F5F4',
  bgElevated: '#FFFFFF',
  bgBrand: '#0A0A0A',
  bgBrandSubtle: '#EEEFFE',
  bgDangerSubtle: '#FCEDED',
  bgInfoSubtle: '#EBF3FB',
  bgSuccessSubtle: '#E6F8EE',
  bgWarningSubtle: '#FFF7E0',
  borderDefault: '#E7E5E4',
  borderStrong: '#D6D3D1',
  borderBrand: '#494FDF',
  textPrimary: '#1C1917',
  textBody: '#292524',
  textMuted: '#78716C',
  textDisabled: '#A8A29E',
  textBrand: '#494FDF',
  primary50: '#F4F5FE',
  primary200: '#C9CCFB',
  primary600: '#494FDF',
  primary700: '#3A3EB3',
  primary800: '#2D3094',
  error: '#E23B4A',
  errorHover: '#C12B39',
  warning: '#E8A500',
  success: '#1FA56B',
  info: '#2563EB',
  radiusSm: '4px',
  radiusMd: '6px',
  shadowSm: '0 1px 2px 0 rgba(26,25,23,0.06), 0 1px 4px 0 rgba(26,25,23,0.04)',
  shadowMd: '0 4px 8px -2px rgba(26,25,23,0.08), 0 2px 6px -2px rgba(26,25,23,0.06)',
  shadowLg: '0 12px 24px -4px rgba(26,25,23,0.10), 0 4px 10px -4px rgba(26,25,23,0.08)',
  fontSans: "'Satoshi', 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
}

/* ============================================================================
 * Building blocks
 * ========================================================================= */
const Shell = styled.div`
  /* Full-viewport takeover. Without this the interview renders at whatever width
     its mount point allows — full width on the standalone invite route, but
     squeezed into the logged-in Assessments module's constrained content column
     (the "half-width for some users" bug). Breaking out to the viewport (mirrors
     the Role-Based instruction screen) makes the layout identical on every entry
     path. */
  position: fixed;
  inset: 0;
  width: 100vw;
  height: 100vh;
  overflow: hidden auto;
  z-index: 1000;
  background: ${T.bgPage};
  color: ${T.textBody};
  font-family: ${T.fontSans};
  -webkit-font-smoothing: antialiased;
`

const TopBar = styled.div`
  position: sticky;
  top: 0;
  z-index: 40;
  height: 58px;
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 0 22px;
  background: rgba(255, 255, 255, 0.86);
  backdrop-filter: blur(10px);
  -webkit-backdrop-filter: blur(10px);
  border-bottom: 1px solid ${T.borderDefault};
`
const TBBrand = styled.div`
  font-weight: 700;
  font-size: 15px;
  color: ${T.textPrimary};
  letter-spacing: -0.01em;
`
const TBRole = styled.div`
  font-size: 12.5px;
  color: ${T.textMuted};
  margin-top: 1px;
  max-width: 240px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`

const Wrap = styled.div`
  max-width: 760px;
  margin: 0 auto;
  padding: 48px 24px 96px;
  animation: fade 0.35s ease;

  @keyframes fade {
    from { opacity: 0; transform: translateY(8px); }
    to   { opacity: 1; transform: none; }
  }

  @media (max-width: 640px) {
    padding: 32px 16px 80px;
  }
`

const Eyebrow = styled.div`
  font-size: 12px;
  font-weight: 700;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: ${T.textBrand};
`

const BackLink = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 7px;
  font-size: 13.5px;
  font-weight: 600;
  color: ${T.textMuted};
  background: none;
  border: none;
  cursor: pointer;
  padding: 0;
  margin-bottom: 18px;
  font-family: inherit;

  &:hover { color: ${T.textBody}; }
  svg { width: 16px; height: 16px; }
`

const Btn = styled(Button)`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  height: 50px;
  padding: 0 28px;
  border-radius: ${T.radiusMd};
  font-size: 15px;
  font-weight: 600;
  font-family: inherit;
  background: ${T.primary600};
  border-color: ${T.primary600};
  color: #fff;
  box-shadow: none;
  &:hover, &:focus { background: ${T.primary700}; border-color: ${T.primary700}; color: #fff; }
  &:active        { background: ${T.primary800}; border-color: ${T.primary800}; }
  &:disabled      { opacity: 0.45; cursor: not-allowed; }
  svg { width: 18px; height: 18px; }
`
const GhostBtn = styled(Button)`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  height: 44px;
  padding: 0 24px;
  border-radius: ${T.radiusMd};
  font-size: 15px;
  font-weight: 600;
  background: ${T.bgElevated};
  color: ${T.textBody};
  box-shadow: inset 0 0 0 1px ${T.borderDefault};
  border: none;
  font-family: inherit;
  &:hover, &:focus { background: ${T.bgSurface}; color: ${T.textBody}; }
`

/* ============================================================================
 * WELCOME stage
 * ========================================================================= */
const Welcome = styled.div`
  h1 {
    font-size: 32px;
    line-height: 1.12;
    margin: 14px 0 12px;
    color: ${T.textPrimary};
    font-weight: 700;
    letter-spacing: -0.02em;

    @media (max-width: 640px) { font-size: 26px; }
  }
  .lede {
    font-size: 17px;
    color: ${T.textMuted};
    max-width: 54ch;
    margin: 0;
  }
`

const RoleHead = styled.div`
  display: flex;
  align-items: center;
  gap: 14px;
  margin-top: 6px;

  .co {
    width: 46px;
    height: 46px;
    border-radius: ${T.radiusMd};
    background: ${T.bgBrand};
    color: #fff;
    display: grid;
    place-items: center;
    font-weight: 700;
    font-size: 18px;
    flex: none;
    letter-spacing: -0.02em;
  }
  .rt {
    font-size: 13px;
    color: ${T.textMuted};
    font-weight: 500;
  }
  .rt b {
    display: block;
    font-size: 18px;
    color: ${T.textPrimary};
    font-weight: 700;
    letter-spacing: -0.02em;
  }
`

const Expect = styled.div`
  margin-top: 30px;
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 14px;

  @media (max-width: 640px) {
    grid-template-columns: 1fr;
  }
`
const Ex = styled.div`
  display: flex;
  gap: 12px;
  align-items: flex-start;
  padding: 16px;
  border: 1px solid ${T.borderDefault};
  border-radius: ${T.radiusMd};

  .i {
    width: 34px;
    height: 34px;
    border-radius: ${T.radiusMd};
    background: ${T.bgBrandSubtle};
    color: ${T.primary600};
    display: grid;
    place-items: center;
    flex: none;
  }
  .i svg { width: 18px; height: 18px; }
  .t {
    font-size: 14px;
    color: ${T.textBody};
    line-height: 1.45;
  }
  .t b {
    display: block;
    color: ${T.textPrimary};
    font-weight: 600;
    margin-bottom: 2px;
  }
`

const Notice = styled.div`
  margin-top: 24px;
  display: flex;
  gap: 13px;
  background: ${T.bgSurface};
  border: 1px solid ${T.borderDefault};
  border-radius: ${T.radiusMd};
  padding: 16px 18px;

  svg { width: 19px; height: 19px; color: ${T.textMuted}; flex: none; margin-top: 1px; }
  .nx { font-size: 13.5px; color: ${T.textMuted}; line-height: 1.55; }
  .nx b { color: ${T.textBody}; font-weight: 600; }
`

const CtaRow = styled.div`
  margin-top: 30px;
  display: flex;
  align-items: center;
  gap: 16px;
  flex-wrap: wrap;
`

const SetupHead = styled.div`
  ${Eyebrow} { display: block; margin-bottom: 6px; }
  h1 {
    font-size: 26px;
    margin: 0 0 8px;
    color: ${T.textPrimary};
    font-weight: 700;
    letter-spacing: -0.02em;
  }
  .lede {
    color: ${T.textMuted};
    font-size: 15.5px;
    max-width: 54ch;
    margin: 0;
    line-height: 1.5;
  }
`

/* ============================================================================
 * READINESS — camera tile + mic meter + status checks
 * ========================================================================= */
const ReadyGrid = styled.div`
  margin-top: 26px;
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 24px;
  align-items: start;

  @media (max-width: 720px) {
    grid-template-columns: 1fr;
  }
`
const Cam = styled.div`
  position: relative;
  aspect-ratio: 4 / 3;
  border-radius: ${T.radiusMd};
  overflow: hidden;
  background: radial-gradient(120% 120% at 50% 18%, #23242E, #101016);
  display: grid;
  place-items: center;

  .sil {
    width: 104px; height: 104px;
    border-radius: 999px;
    background: rgba(255,255,255,0.05);
    border: 1px solid rgba(255,255,255,0.08);
    display: grid; place-items: center;
  }
  .sil svg { width: 56px; height: 56px; color: rgba(255,255,255,0.32); }
  .frame {
    position: absolute; inset: 14px;
    border: 2px dashed rgba(255,255,255,0.16);
    border-radius: ${T.radiusMd};
    pointer-events: none;
  }
  .tag {
    position: absolute; bottom: 12px; left: 12px;
    font-size: 11px; font-weight: 600; color: #fff;
    background: rgba(0,0,0,0.45);
    padding: 5px 10px;
    border-radius: ${T.radiusSm};
  }
`
const Meter = styled.div`
  margin-top: 14px;

  .ml {
    font-size: 12px; font-weight: 600;
    color: ${T.textMuted};
    margin-bottom: 8px;
    display: flex; align-items: center; gap: 7px;
  }
  .ml svg { width: 14px; height: 14px; }
`
const Bars = styled.div`
  display: flex;
  gap: 3px;
  align-items: flex-end;
  height: 26px;
  i {
    flex: 1;
    background: ${T.borderStrong};
    border-radius: 2px;
    height: 30%;
    transition: height 0.12s, background 0.12s;
    display: block;
  }
  i.lit { background: ${T.success}; }
`

const CardPad = styled.div`
  background: ${T.bgElevated};
  border: 1px solid ${T.borderDefault};
  border-radius: ${T.radiusMd};
  box-shadow: ${T.shadowSm};
  padding: 28px;

  @media (max-width: 640px) {
    padding: 22px 18px;
  }
`

const Checks = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
`
const spin = keyframes`to { transform: rotate(360deg); }`
const Check = styled.div`
  display: flex;
  align-items: center;
  gap: 13px;
  padding: 13px 4px;
  border-bottom: 1px solid ${T.borderDefault};
  &:last-child { border-bottom: none; }

  .ic {
    flex: none;
    width: 36px; height: 36px;
    border-radius: ${T.radiusMd};
    display: grid; place-items: center;
    background: ${T.bgSurface};
    color: ${T.textMuted};
    transition: 0.2s;
  }
  .ic svg { width: 18px; height: 18px; }
  &.ok .ic { background: ${T.bgSuccessSubtle}; color: ${T.success}; }

  .lbl { font-size: 14px; font-weight: 600; color: ${T.textPrimary}; }
  .sub { font-size: 12.5px; color: ${T.textMuted}; margin-top: 1px; }

  .st {
    margin-left: auto;
    font-size: 12.5px; font-weight: 700;
    color: ${T.textDisabled};
    display: flex; align-items: center; gap: 6px;
  }
  &.ok .st { color: ${T.success}; }
  .st svg { width: 15px; height: 15px; }
  .spin {
    width: 15px; height: 15px;
    border: 2px solid ${T.borderStrong};
    border-top-color: ${T.primary600};
    border-radius: 50%;
    animation: ${spin} 0.7s linear infinite;
  }
`

const BtnBlock = styled(Btn)`
  width: 100%;
  margin-top: 22px;
`

/* ============================================================================
 * RESUME stage
 * ========================================================================= */
const ResumeDrop = styled.div`
  display: flex;
  align-items: center;
  gap: 14px;
  padding: 22px;
  border: 1.5px dashed ${T.borderStrong};
  border-radius: ${T.radiusMd};
  background: ${T.bgSurface};
  cursor: pointer;
  transition: 0.15s;
  margin-top: 22px;

  &:hover,
  &.drag { border-color: ${T.borderBrand}; background: ${T.bgBrandSubtle}; }

  .rd-ic {
    width: 46px; height: 46px;
    border-radius: ${T.radiusMd};
    background: ${T.bgElevated};
    box-shadow: ${T.shadowSm};
    display: grid; place-items: center;
    color: ${T.textBrand};
    flex: none;
  }
  .rd-ic svg { width: 22px; height: 22px; }

  .rd-main {
    font-size: 15px;
    font-weight: 600;
    color: ${T.textPrimary};
    display: flex; flex-direction: column; gap: 2px;
  }
  .rd-main small {
    font-size: 12.5px; font-weight: 400;
    color: ${T.textMuted};
  }
`
const ResumeFile = styled.div`
  margin-top: 14px;
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 13px 15px;
  border: 1px solid ${T.borderDefault};
  border-radius: ${T.radiusMd};
  background: ${T.bgElevated};

  .fi {
    width: 38px; height: 38px;
    border-radius: ${T.radiusMd};
    background: ${T.bgSuccessSubtle};
    color: ${T.success};
    display: grid; place-items: center;
    flex: none;
  }
  .fi svg { width: 18px; height: 18px; }
  .fn { font-size: 14px; font-weight: 600; color: ${T.textPrimary}; }
  .fm { font-size: 12.5px; color: ${T.textMuted}; margin-top: 1px; }
  .fx {
    margin-left: auto;
    color: ${T.textMuted};
    width: 30px; height: 30px;
    border-radius: ${T.radiusMd};
    display: grid; place-items: center;
    background: none;
    border: none;
    cursor: pointer;
  }
  .fx:hover { background: ${T.bgSurface}; color: ${T.error}; }
  .fx svg { width: 17px; height: 17px; }
`
const ResumeWhy = styled.div`
  margin-top: 18px;
  display: flex;
  gap: 11px;
  align-items: flex-start;
  background: ${T.bgBrandSubtle};
  border-radius: ${T.radiusMd};
  padding: 13px 15px;
  font-size: 13.5px;
  color: ${T.textBody};
  line-height: 1.5;
  max-width: 600px;

  svg { width: 17px; height: 17px; color: ${T.textBrand}; flex: none; margin-top: 1px; }
  b { font-weight: 600; }
`

/* ============================================================================
 * Fullscreen / Camera-mic permission modals
 * ========================================================================= */
const Overlay = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(15, 23, 42, 0.55);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 99999;
  padding: 20px;
  animation: fade 0.2s ease;

  @keyframes fade {
    from { opacity: 0; }
    to   { opacity: 1; }
  }
`
const ModalCard = styled.div`
  background: ${T.bgElevated};
  border-radius: ${T.radiusMd};
  padding: 28px 32px;
  max-width: 520px;
  width: 100%;
  box-shadow: ${T.shadowLg};
  border: 1px solid ${T.borderDefault};

  h2 {
    font-size: 20px;
    font-weight: 700;
    color: ${T.textPrimary};
    margin: 0 0 8px;
    letter-spacing: -0.01em;
  }
  p, ul, li { color: ${T.textBody}; font-size: 14.5px; line-height: 1.55; }
  ul { padding-left: 20px; margin: 8px 0 0; }
  li { margin-bottom: 4px; }
`

/* ============================================================================
 * Component
 * ========================================================================= */
const STAGE_WELCOME = 'welcome'
const STAGE_READY = 'readiness'
const STAGE_BIOMETRIC = 'biometric'
const STAGE_RESUME = 'resume'

const ChevronRight = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M5 12h14M13 6l6 6-6 6" />
  </svg>
)
const ArrowLeft = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M19 12H5M11 6l-6 6 6 6" />
  </svg>
)
const ClockIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="12" cy="12" r="9" />
    <path d="M12 7v5l3 2" />
  </svg>
)
const ChatIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M8 10h8M8 14h5" />
    <path d="M21 12a9 9 0 1 1-3.5-7.1L21 4v5h-5" />
  </svg>
)
const CamIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <rect x="2" y="6" width="14" height="12" rx="2" />
    <path d="m16 10 6-3v10l-6-3" />
  </svg>
)
const MicIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M12 3a4 4 0 0 0-4 4v3a4 4 0 0 0 8 0V7a4 4 0 0 0-4-4Z" />
    <path d="M5 11a7 7 0 0 0 14 0M12 18v3" />
  </svg>
)
const ConvIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M21 11.5a8.4 8.4 0 0 1-8.5 8.5 8.4 8.4 0 0 1-3.8-.9L3 21l1.9-5.7A8.4 8.4 0 0 1 4 11.5 8.5 8.5 0 0 1 12.5 3 8.5 8.5 0 0 1 21 11.5Z" />
  </svg>
)
const ShieldIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M12 3 4 6v6c0 5 3.5 8 8 9 4.5-1 8-4 8-9V6l-8-3Z" />
  </svg>
)
const BrowserIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="12" cy="12" r="9" />
    <path d="M3 12h18M12 3a14 14 0 0 1 0 18 14 14 0 0 1 0-18Z" />
  </svg>
)
const NetIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M5 12.5 9 9l4 5 6-7" />
    <path d="M3 20h18" />
  </svg>
)
const CheckIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="20 6 9 17 4 12" />
  </svg>
)
const UploadIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M12 13v8M8 17l4 4 4-4" />
    <path d="M20 16.7A5 5 0 0 0 18 7h-1.3A8 8 0 1 0 4 15" />
  </svg>
)
const SparkleIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M12 3v4M12 17v4M3 12h4M17 12h4M6 6l2.5 2.5M15.5 15.5 18 18M18 6l-2.5 2.5M8.5 15.5 6 18" />
  </svg>
)
const XIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M18 6 6 18M6 6l12 12" />
  </svg>
)
const FileIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
    <polyline points="14 2 14 8 20 8" />
  </svg>
)

const initialsFromCompany = (s) => {
  if (!s) return 'PL'
  const words = String(s).trim().split(/\s+/).slice(0, 2)
  return words.map((w) => w[0]?.toUpperCase() || '').join('') || 'PL'
}

const AIInterviewInstructions = ({ assessment, setAssessment }) => {
  const navigate = useNavigate()
  const studentDetails = useSelector((s) => s?.studentData?.studentDetails)
  // OTP invite candidates have no portal dashboard; logged-in students do.
  const isInvite = !!readInviteScopedJwt()
  const otpAssignedId =
    (typeof sessionStorage !== 'undefined' &&
      (sessionStorage.getItem('assessment_invite_assigned_id') ||
        sessionStorage.getItem('ai_interview_assessment_assigned_id'))) || null
  const otpCompany =
    (typeof sessionStorage !== 'undefined' &&
      (sessionStorage.getItem('assessment_invite_company') ||
        sessionStorage.getItem('ai_interview_company'))) || ''
  const otpRole =
    (typeof sessionStorage !== 'undefined' &&
      (sessionStorage.getItem('assessment_invite_role') ||
        sessionStorage.getItem('ai_interview_role'))) || ''
  const studentId =
    studentDetails?.id || otpAssignedId || assessment?.assessment_assigned_id || null

  const [stage, setStage] = useState(STAGE_WELCOME)
  const [resumeText, setResumeText] = useState('')
  const [resumeFileName, setResumeFileName] = useState('')
  const [resumeFileSize, setResumeFileSize] = useState(0)
  const [resumeUploading, setResumeUploading] = useState(false)

  // Single entry point for resume selection — used by both the click-to-open
  // dialog and drag-and-drop (antd Dragger). See resumeUpload.js for why this is
  // a shared, testable handler rather than inline duplicated logic.
  const handleResumeFile = (file) =>
    runResumeUpload(file, {
      parseResume,
      message,
      setResumeUploading,
      setResumeText,
      setResumeFileName,
      setResumeFileSize,
    })

  /* ------------------------------------------------------------------------
   * Readiness checks — live camera + mic in the design's tile, statuses for
   * Camera / Microphone / Browser / Network. Camera passes only when the
   * FastAPI face check (/proctoring/verify-frame) sees a face; Microphone
   * passes only when /proctoring/detect-audio hears a human voice.
   * --------------------------------------------------------------------- */
  const [checks, setChecks] = useState({
    camera: { status: 'checking', sub: 'Checking…' },
    mic: { status: 'checking', sub: 'Checking…' },
    browser: { status: 'checking', sub: 'Checking…' },
    network: { status: 'checking', sub: 'Checking…' },
  })
  const [micBars, setMicBars] = useState(0) // 0..14 lit bars
  const readyStreamRef = useRef(null)
  const readyVideoRef = useRef(null)
  const readyRafRef = useRef(null)
  const readyAudioCtxRef = useRef(null)
  const faceCheckStartedRef = useRef(false)
  const micCheckStartedRef = useRef(false)

  const setCheck = (key, status, sub) =>
    setChecks((c) => ({ ...c, [key]: { status, sub } }))

  const allChecksComplete = Object.values(checks).every((c) => c.status === 'ok')

  // Multi-codec-safe base64: MediaRecorder mimes like 'audio/webm;codecs=opus'
  // put a comma inside the codec spec, so split(',')[1] is wrong — slice from
  // the ';base64,' marker instead.
  const readyBlobToBase64 = (blob) =>
    new Promise((resolve, reject) => {
      const reader = new FileReader()
      reader.onloadend = () => {
        const result = reader.result || ''
        const marker = ';base64,'
        const idx = result.indexOf(marker)
        resolve(idx >= 0 ? result.slice(idx + marker.length) : '')
      }
      reader.onerror = reject
      reader.readAsDataURL(blob)
    })

  // On any entry to the instruction flow — including a reload that lands an
  // already-completed OTP candidate on the "already attempted" screen — resume
  // any recording still queued in IndexedDB from a prior tab that closed before
  // its upload finished.
  useEffect(() => {
    initUploadQueueResumption()
  }, [])

  useEffect(() => {
    if (stage !== STAGE_READY) return undefined
    let cancelled = false
    faceCheckStartedRef.current = false
    micCheckStartedRef.current = false
    setChecks({
      camera: { status: 'checking', sub: 'Requesting permission…' },
      mic: { status: 'checking', sub: 'Requesting permission…' },
      browser: { status: 'checking', sub: 'Checking…' },
      network: { status: 'checking', sub: 'Checking…' },
    })
    setMicBars(0)

    // --- Browser: feature support, instant
    const hasMedia = !!(navigator.mediaDevices && navigator.mediaDevices.getUserMedia)
    const hasRecorder = typeof MediaRecorder !== 'undefined'
    if (hasMedia && hasRecorder) setCheck('browser', 'ok', 'Fully supported')
    else setCheck('browser', 'fail', 'Use Chrome or Edge on a laptop')

    // --- Network: round-trip the config endpoint and time it
    const netStart = Date.now()
    fetchConfigInfo(assessment?.assessment_assigned_id)
      .then(() => {
        if (cancelled) return
        const ms = Date.now() - netStart
        setCheck('network', 'ok', ms < 1200 ? 'Connection looks good' : `Reachable (${ms} ms)`)
      })
      .catch(() => {
        if (cancelled) return
        setCheck('network', 'fail', 'Could not reach the server')
      })

    // --- Camera + Microphone via one getUserMedia
    ;(async () => {
      let stream
      try {
        stream = await navigator.mediaDevices.getUserMedia({ video: true, audio: true })
      } catch (err) {
        if (cancelled) return
        setCheck('camera', 'fail', 'Permission denied — allow camera access')
        setCheck('mic', 'fail', 'Permission denied — allow mic access')
        return
      }
      if (cancelled) {
        stream.getTracks().forEach((t) => t.stop())
        return
      }
      readyStreamRef.current = stream
      if (readyVideoRef.current) {
        readyVideoRef.current.srcObject = stream
        readyVideoRef.current.muted = true
        readyVideoRef.current.play?.().catch(() => {})
      }
      setCheck('camera', 'checking', 'Camera on — checking your face…')
      setCheck('mic', 'checking', 'Say something to test your mic')

      // ---- Face check: wait for real frames, grab one, send to verify-frame
      const tryFaceCheck = async () => {
        if (cancelled || faceCheckStartedRef.current) return
        const v = readyVideoRef.current
        if (!v || v.videoWidth === 0) {
          setTimeout(tryFaceCheck, 300)
          return
        }
        faceCheckStartedRef.current = true
        try {
          const canvas = document.createElement('canvas')
          canvas.width = v.videoWidth
          canvas.height = v.videoHeight
          canvas.getContext('2d').drawImage(v, 0, 0, canvas.width, canvas.height)
          const imageBase64 = canvas.toDataURL('image/jpeg', 0.8).split(',')[1]
          const resp = await axios.post(
            `${process.env.REACT_APP_FASTAPI_URL}/proctoring/verify-frame`,
            {
              student_id: studentId,
              frame_number: 1,
              image_data: imageBase64,
              min_confidence: 0.5,
            },
            { timeout: 60000 }
          )
          if (cancelled) return
          if (resp.data?.success && resp.data?.face_detected) {
            setCheck('camera', 'ok', 'Face visible and clear')
          } else {
            // retry once after 2s — candidate may still be settling into frame
            faceCheckStartedRef.current = false
            setCheck('camera', 'checking', 'Face not detected — sit facing the camera')
            setTimeout(tryFaceCheck, 2000)
          }
        } catch (err) {
          if (cancelled) return
          faceCheckStartedRef.current = false
          setCheck('camera', 'checking', 'Rechecking your face…')
          setTimeout(tryFaceCheck, 2500)
        }
      }
      tryFaceCheck()

      // ---- Mic bars + voice check
      try {
        const AudioCtx = window.AudioContext || window.webkitAudioContext
        const ctx = new AudioCtx()
        readyAudioCtxRef.current = ctx
        const source = ctx.createMediaStreamSource(stream)
        const analyser = ctx.createAnalyser()
        analyser.fftSize = 256
        source.connect(analyser)
        const data = new Uint8Array(analyser.frequencyBinCount)

        const loop = () => {
          if (cancelled) return
          analyser.getByteFrequencyData(data)
          let sum = 0
          for (let i = 0; i < data.length; i += 1) sum += data[i]
          const level = sum / data.length / 255 // 0..1
          setMicBars(Math.min(14, Math.round(level * 40)))

          // First time we hear real signal, record a short clip and run the
          // server-side voice check.
          if (level > 0.06 && !micCheckStartedRef.current) {
            micCheckStartedRef.current = true
            setCheck('mic', 'checking', 'Heard you — verifying voice…')
            try {
              const audioStream = new MediaStream(stream.getAudioTracks())
              const rec = new MediaRecorder(audioStream)
              const chunks = []
              rec.ondataavailable = (e) => {
                if (e.data.size > 0) chunks.push(e.data)
              }
              rec.onstop = async () => {
                if (cancelled) return
                try {
                  const blob = new Blob(chunks, { type: rec.mimeType || 'audio/webm' })
                  const audioBase64 = await readyBlobToBase64(blob)
                  const resp = await axios.post(
                    `${process.env.REACT_APP_FASTAPI_URL}/proctoring/detect-audio`,
                    { student_id: studentId, audio_data: audioBase64 },
                    { timeout: 60000 }
                  )
                  if (cancelled) return
                  if (resp.data?.success && resp.data?.audio_detected) {
                    setCheck('mic', 'ok', 'Voice detected, audio clear')
                  } else {
                    micCheckStartedRef.current = false
                    setCheck('mic', 'checking', 'Could not hear a voice — try speaking again')
                  }
                } catch (err) {
                  if (cancelled) return
                  micCheckStartedRef.current = false
                  setCheck('mic', 'checking', 'Voice check hiccuped — speak again')
                }
              }
              rec.start()
              setTimeout(() => {
                try { if (rec.state !== 'inactive') rec.stop() } catch (_) {}
              }, 3500)
            } catch (err) {
              // MediaRecorder failed — accept the analyser signal as proof of mic
              setCheck('mic', 'ok', 'Microphone is picking up sound')
            }
          }
          readyRafRef.current = requestAnimationFrame(loop)
        }
        readyRafRef.current = requestAnimationFrame(loop)
      } catch (err) {
        if (!cancelled) setCheck('mic', 'fail', 'Could not read microphone input')
      }
    })()

    return () => {
      cancelled = true
      if (readyRafRef.current) cancelAnimationFrame(readyRafRef.current)
      readyRafRef.current = null
      try { readyStreamRef.current?.getTracks().forEach((t) => t.stop()) } catch (_) {}
      readyStreamRef.current = null
      try { readyAudioCtxRef.current?.close() } catch (_) {}
      readyAudioCtxRef.current = null
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stage])

  const verificationEnabled = assessment?.allowVerification !== false

  const [isTimerActive, setIsTimerActive] = useState(false)
  const [timerPhase, setTimerPhase] = useState('initial')
  const [countdown, setCountdown] = useState(3)

  const [showFullscreenModal, setShowFullscreenModal] = useState(false)
  const [showInterview, setShowInterview] = useState(false)

  const [interviewDurationSec, setInterviewDurationSec] = useState(null)
  const [resumePolicy, setResumePolicy] = useState(
    assessment?.resumePolicy || 'optional'
  )
  // Single-attempt server gate: getConfigInfo returns the assignment status.
  // If the interview was already started/submitted (INPROGRESS / DROPOUT /
  // attempted / submitted), block the instructions screen on reload so the
  // candidate can't restart — they only get one attempt.
  const [attemptBlocked, setAttemptBlocked] = useState(false)

  // The localStorage "completed" flag is only a CACHE of the status below — it
  // exists so a reload doesn't flash the welcome screen before the fetch lands.
  // An admin "resend invite" resets the SAME assessment_assigned_id back to
  // PENDING, so the cache can legitimately go stale; it used to be write-only,
  // which permanently locked resent candidates out of their retake. The server
  // is the only source of truth: nothing acts on the cached value until
  // statusResolved, and a fresh assignment clears it.
  const guardAssignedId = assessment?.assessment_assigned_id || otpAssignedId
  const [alreadyCompleted, setAlreadyCompleted] = useState(() =>
    isAiInterviewCompleted(guardAssignedId)
  )
  const [statusResolved, setStatusResolved] = useState(false)

  useEffect(() => {
    const id = guardAssignedId
    // Nothing to verify — resolve immediately so the guards below never hang.
    if (!id) {
      setStatusResolved(true)
      return undefined
    }
    let cancelled = false
    fetchConfigInfo(id)
      .then((resp) => {
        if (cancelled) return
        const body = resp?.data?.data || resp?.data || {}
        const sec = body.interviewDuration
        if (typeof sec === 'number' && sec > 0) setInterviewDurationSec(sec)
        if (body.resumePolicy && VALID_RESUME_POLICIES.has(body.resumePolicy)) {
          setResumePolicy(body.resumePolicy)
        }
        // Pass the resolved candidate identity through to the live interview
        // so its TopBar can show the candidate's name (not the assessment/role
        // name). setAssessment is a no-op if it's a logout/logback path.
        if (body.candidateName && typeof setAssessment === 'function') {
          setAssessment((a) => (a ? { ...a, candidateName: body.candidateName } : a))
        }
        const st = String(body.status || '').toUpperCase()
        if (
          st === 'INPROGRESS' ||
          st === 'DROPOUT' ||
          st === 'COMPLETED' ||
          body.attempted === true ||
          body.submitted === true
        ) {
          // Mark completed locally too so a further reload short-circuits even
          // before the config fetch returns.
          markAiInterviewCompleted(id)
          setAttemptBlocked(true)
          setAlreadyCompleted(true)
        } else if (st === 'PENDING') {
          // Affirmatively takeable — an admin resend reset this assignment, so
          // drop the stale cache and let the candidate through. Only 'PENDING'
          // counts: getConfigInfo returns status `null` when its status lookup
          // fails, and that must NOT be read as "takeable".
          clearAiInterviewCompleted(id)
          setAlreadyCompleted(false)
          setAttemptBlocked(false)
        }
        setStatusResolved(true)
      })
      .catch((err) => {
        console.warn('[ai-interview instructions] config fetch failed:', err?.message || err)
        // Fail closed: keep whatever the cache said rather than opening a retake.
        setStatusResolved(true)
      })
    return () => { cancelled = true }
  }, [guardAssignedId])

  // Reload guard: if this assignment was already finished (flag persisted by
  // interview.js on completeSession), never restart the instructions/welcome
  // flow. A logged-in student is bounced to their dashboard (where it now shows
  // under Completed); an OTP candidate sees an "already done" notice below.
  // Waits for statusResolved so a stale cache can't bounce a resent candidate
  // before the server has had its say.
  useEffect(() => {
    if (statusResolved && alreadyCompleted && !isInvite) {
      if (typeof setAssessment === 'function') setAssessment(null)
      navigate('/assessment')
    }
  }, [statusResolved, alreadyCompleted, isInvite, setAssessment, navigate])

  const durationMinutes = interviewDurationSec
    ? Math.max(1, Math.round(interviewDurationSec / 60))
    : null
  const durationCopy = durationMinutes
    ? `About ${durationMinutes} minutes`
    : 'About 15–20 minutes'

  /* ---------------------------------------------------------------------- */
  /* Stage navigation                                                       */
  /* ---------------------------------------------------------------------- */
  const goReady = () => setStage(STAGE_READY)
  const goWelcome = () => setStage(STAGE_WELCOME)
  // The design has no separate biometric/identity screen — the readiness
  // check (camera + mic + browser + network) IS the only setup stage.
  // Proctoring still runs continuously during the live interview via the
  // existing uploadProctorFrame loop in interview.js, so we lose nothing
  // by collapsing the legacy pre-interview face/voice verification.
  const advanceAfterReady = () => {
    if (resumePolicy === 'not_required') handleStartInterview()
    else setStage(STAGE_RESUME)
  }

  /* ---------------------------------------------------------------------- */
  /* Interview start / fullscreen                                           */
  /* ---------------------------------------------------------------------- */
  const handleStartInterview = () => {
    primeTtsAudio()
    setIsTimerActive(true)
    setTimerPhase('initial')
    setTimeout(() => {
      setTimerPhase('countdown')
      setCountdown(3)
      const interval = setInterval(() => {
        setCountdown((prev) => {
          if (prev <= 1) {
            clearInterval(interval)
            setTimerPhase('start')
            setTimeout(() => setShowFullscreenModal(true), 800)
            return 0
          }
          return prev - 1
        })
      }, 1000)
    }, 1600)
  }

  const requestFullscreen = async () => {
    primeTtsAudio()
    try {
      const el = document.documentElement
      if (el.requestFullscreen) await el.requestFullscreen()
      else if (el.webkitRequestFullscreen) await el.webkitRequestFullscreen()
      else if (el.mozRequestFullScreen) await el.mozRequestFullScreen()
    } catch (err) {
      console.warn('Fullscreen denied or unsupported, continuing:', err?.message)
    }
    setShowFullscreenModal(false)
    setShowInterview(true)
  }

  const handleBack = () => setAssessment && setAssessment(null)

  // Cached "completed" but the server hasn't answered yet. Show a neutral
  // checking state rather than the welcome screen (which the cache exists to
  // avoid flashing) or a terminal notice (which would be wrong for a candidate
  // whose invite was just resent).
  if (!statusResolved && alreadyCompleted) {
    return (
      <div
        style={{
          minHeight: '100vh',
          display: 'grid',
          placeItems: 'center',
          padding: '48px 24px',
          textAlign: 'center',
          fontFamily: 'Inter, system-ui, sans-serif',
          background: '#FFFFFF',
          color: '#78716C',
          fontSize: 15,
        }}
      >
        Checking your interview status…
      </div>
    )
  }

  // Single-attempt server gate: the assignment was already started/submitted
  // (status from getConfigInfo). Block the instructions screen entirely on
  // reload — for ALL candidates (OTP included) — so the interview can't be
  // restarted. One attempt only.
  if (attemptBlocked) {
    return (
      <div
        style={{
          minHeight: '100vh',
          display: 'grid',
          placeItems: 'center',
          padding: '48px 24px',
          textAlign: 'center',
          fontFamily: 'Inter, system-ui, sans-serif',
          background: '#FFFFFF',
        }}
      >
        <div style={{ maxWidth: 480 }}>
          <h1 style={{ fontSize: 24, fontWeight: 700, color: '#1C1917', marginBottom: 12 }}>
            Interview already attempted
          </h1>
          <p style={{ fontSize: 15, color: '#78716C', lineHeight: 1.6 }}>
            This interview has already been started and can only be taken once,
            so it can’t be retaken. Whatever you answered has been recorded for
            the recruiting team — there’s nothing more to do here.
          </p>
          <p style={{ fontSize: 15, color: '#78716C', lineHeight: 1.6, marginTop: 14 }}>
            You can now close this window.
          </p>
        </div>
      </div>
    )
  }

  // Already-completed reload guard. Only reachable once statusResolved (the
  // checking state above returns first), so this is the server's verdict, not a
  // stale cache. Logged-in students are being redirected by the effect above, so
  // render nothing to avoid flashing the welcome screen. OTP candidates (no
  // dashboard) get a terminal "interview complete" notice.
  if (alreadyCompleted) {
    if (!isInvite) return null
    return (
      <div
        style={{
          minHeight: '100vh',
          display: 'grid',
          placeItems: 'center',
          padding: '48px 24px',
          textAlign: 'center',
          fontFamily: 'Inter, system-ui, sans-serif',
          background: '#FFFFFF',
        }}
      >
        <div style={{ maxWidth: 460 }}>
          <div
            style={{
              width: 64,
              height: 64,
              borderRadius: 999,
              background: '#E6F8EE',
              color: '#16A34A',
              display: 'grid',
              placeItems: 'center',
              margin: '0 auto 20px',
            }}
          >
            <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round">
              <path d="M20 7 9 18l-5-5" />
            </svg>
          </div>
          <h1 style={{ fontSize: 24, fontWeight: 700, color: '#1C1917', marginBottom: 10 }}>
            Your interview is already complete
          </h1>
          <p style={{ fontSize: 15, color: '#78716C', lineHeight: 1.55 }}>
            Your responses have been submitted to the recruiting team. You can now
            close this window.
          </p>
        </div>
      </div>
    )
  }

  if (showInterview) {
    return (
      <Interview
        assessment={assessment}
        setAssessment={setAssessment}
        resumeText={resumeText}
      />
    )
  }

  const company =
    otpCompany || assessment?.company || assessment?.corporateName || 'Your role'
  const role = otpRole || assessment?.roleName || assessment?.title || 'AI Interview'
  const companyInitials = initialsFromCompany(company === 'Your role' ? role : company)

  /* ====================================================================== */
  /* WELCOME                                                                 */
  /* ====================================================================== */
  const renderWelcome = () => (
    <Wrap>
      <Welcome>
        <Eyebrow>AI-led screening interview</Eyebrow>
        <h1>Your AI interview</h1>
        <RoleHead>
          <div className="co">{companyInitials}</div>
          <div className="rt">
            Role you applied for
            <b>{[role, company !== 'Your role' ? company : null].filter(Boolean).join(' · ')}</b>
          </div>
        </RoleHead>

        <Expect>
          <Ex>
            <span className="i"><ClockIcon /></span>
            <span className="t">
              <b>{durationCopy}</b>
              At your own pace. No countdown.
            </span>
          </Ex>
          <Ex>
            <span className="i"><ChatIcon /></span>
            <span className="t">
              <b>Conversational, one at a time</b>
              The AI listens and replies. Speak naturally.
            </span>
          </Ex>
          <Ex>
            <span className="i"><CamIcon /></span>
            <span className="t">
              <b>Camera &amp; microphone</b>
              You answer by speaking. Setup check first.
            </span>
          </Ex>
          <Ex>
            <span className="i"><ConvIcon /></span>
            <span className="t">
              <b>Just a conversation</b>
              No trick questions. Answer in your own words.
            </span>
          </Ex>
        </Expect>

        <Notice>
          <ShieldIcon />
          <span className="nx">
            <b>Your privacy.</b> Recorded for the recruiting team
            {company && company !== 'Your role' ? ` at ${company}` : ''}.
            A person decides, never the AI.
          </span>
        </Notice>

        <CtaRow>
          <Btn onClick={goReady}>
            Begin readiness check <ChevronRight />
          </Btn>
        </CtaRow>
      </Welcome>
    </Wrap>
  )

  /* ====================================================================== */
  /* READINESS                                                               */
  /* ====================================================================== */
  const renderReadiness = () => (
    <Wrap>
      <BackLink onClick={goWelcome}>
        <ArrowLeft /> Back to start
      </BackLink>
      <SetupHead>
        <Eyebrow>Step 1 of 2 · Setup</Eyebrow>
        <h1>Camera and mic check</h1>
        <p className="lede">A quick check so nothing breaks mid-interview.</p>
      </SetupHead>

      <ReadyGrid>
        <div>
          <Cam>
            {/* Live preview replaces the design's silhouette placeholder. The
                silhouette only shows until the camera stream attaches. */}
            <video
              ref={readyVideoRef}
              autoPlay
              muted
              playsInline
              style={{
                position: 'absolute',
                inset: 0,
                width: '100%',
                height: '100%',
                objectFit: 'cover',
                transform: 'scaleX(-1)',
              }}
            />
            {checks.camera.status === 'checking' && !readyStreamRef.current && (
              <div className="sil">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6">
                  <circle cx="12" cy="8" r="4" />
                  <path d="M4 21c0-4 3.5-7 8-7s8 3 8 7" />
                </svg>
              </div>
            )}
            <div className="frame" />
            <div className="tag">Your camera preview</div>
            {/* Explicit face-detection confirmation badge, top-right of the tile */}
            <div
              style={{
                position: 'absolute',
                top: 12,
                right: 12,
                display: 'inline-flex',
                alignItems: 'center',
                gap: 6,
                fontSize: 11.5,
                fontWeight: 700,
                padding: '5px 10px',
                borderRadius: T.radiusSm,
                background:
                  checks.camera.status === 'ok'
                    ? 'rgba(31,165,107,0.92)'
                    : checks.camera.status === 'fail'
                    ? 'rgba(226,59,74,0.92)'
                    : 'rgba(0,0,0,0.45)',
                color: '#fff',
                transition: 'background 0.25s ease',
              }}
            >
              {checks.camera.status === 'ok' ? (
                <>
                  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                    <polyline points="20 6 9 17 4 12" />
                  </svg>
                  Face detected
                </>
              ) : checks.camera.status === 'fail' ? (
                'Camera blocked'
              ) : (
                'Detecting face…'
              )}
            </div>
          </Cam>
          <Meter>
            <div className="ml">
              <MicIcon />
              {checks.mic.status === 'ok' ? (
                <span style={{ color: T.success, fontWeight: 700, display: 'inline-flex', alignItems: 'center', gap: 5 }}>
                  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                    <polyline points="20 6 9 17 4 12" />
                  </svg>
                  Voice detected — mic is working
                </span>
              ) : (
                'Say something to test your mic'
              )}
            </div>
            <Bars>
              {Array.from({ length: 14 }).map((_, i) => (
                <i key={i} className={i < micBars ? 'lit' : ''} />
              ))}
            </Bars>
          </Meter>
        </div>

        <CardPad>
          <Checks>
            {[
              { k: 'camera', label: 'Camera', Icon: CamIcon },
              { k: 'mic', label: 'Microphone', Icon: MicIcon },
              { k: 'browser', label: 'Browser', Icon: BrowserIcon },
              { k: 'network', label: 'Network', Icon: NetIcon },
            ].map(({ k, label, Icon }) => {
              const c = checks[k]
              return (
                <Check key={k} className={c.status === 'ok' ? 'ok' : ''}>
                  <span className="ic"><Icon /></span>
                  <div>
                    <div className="lbl">{label}</div>
                    <div className="sub">{c.sub}</div>
                  </div>
                  <span className="st">
                    {c.status === 'ok' ? (
                      <>
                        <CheckIcon /> Ready
                      </>
                    ) : c.status === 'fail' ? (
                      <span style={{ color: T.error }}>Failed</span>
                    ) : (
                      <span className="spin" />
                    )}
                  </span>
                </Check>
              )
            })}
          </Checks>
          <BtnBlock
            disabled={!allChecksComplete}
            onClick={advanceAfterReady}
          >
            Continue <ChevronRight />
          </BtnBlock>
        </CardPad>
      </ReadyGrid>
    </Wrap>
  )

  /* Biometric stage removed — kept as a no-op placeholder so any stray
     deep-link or pushState that targeted it still has somewhere to land. */
  const renderBiometric = () => (
    <Wrap>
      <BackLink onClick={() => setStage(STAGE_READY)}>
        <ArrowLeft /> Back
      </BackLink>
    </Wrap>
  )

  /* ====================================================================== */
  /* RESUME                                                                  */
  /* ====================================================================== */
  const isMandatory = resumePolicy === 'mandatory'
  const renderResume = () => (
    <Wrap>
      <BackLink onClick={() => setStage(STAGE_READY)}>
        <ArrowLeft /> Back
      </BackLink>
      <SetupHead>
        <Eyebrow>Step 2 of 2 · Setup</Eyebrow>
        <h1>
          Add your resume{' '}
          <span style={{ color: T.textMuted, fontWeight: 500 }}>
            {isMandatory ? '(required)' : '(optional)'}
          </span>
        </h1>
        <p className="lede">
          {isMandatory
            ? 'Upload your resume so the AI can tailor questions to your background. It’s required for this role.'
            : 'Attach it and your interviewer asks sharper questions about your experience and skills. You can skip this.'}
        </p>
      </SetupHead>

      {/* Single upload path: antd's Dragger opens the file dialog on click AND
          handles drag-and-drop. Both feed the same shared handler. We return
          false from beforeUpload so antd never auto-uploads — parsing goes to
          FastAPI ourselves. (The old code also had a nested <label>+<input>
          doing a manual .click(), which fought the Dragger and left the dialog
          unreliable — that's removed.) */}
      <Upload.Dragger
        name="resume"
        accept=".pdf,.docx,.txt,application/pdf,application/vnd.openxmlformats-officedocument.wordprocessingml.document,text/plain"
        multiple={false}
        showUploadList={false}
        disabled={resumeUploading}
        style={{ background: 'transparent', border: 'none', padding: 0, marginTop: 22 }}
        beforeUpload={(file) => {
          handleResumeFile(file)
          return false
        }}
      >
        <ResumeDrop>
          <span className="rd-ic"><UploadIcon /></span>
          <span className="rd-main">
            {resumeUploading ? 'Parsing your resume…' : 'Tap to attach your resume'}
            <small>PDF or Word. Drag &amp; drop works too.</small>
          </span>
        </ResumeDrop>
      </Upload.Dragger>

      {resumeFileName && (
        <ResumeFile>
          <span className="fi"><FileIcon /></span>
          <div>
            <div className="fn">{resumeFileName}</div>
            <div className="fm">
              {resumeFileSize ? `${Math.round(resumeFileSize / 1024)} KB` : 'Loaded'} ·{' '}
              {resumeText.length.toLocaleString()} chars
            </div>
          </div>
          <button
            className="fx"
            onClick={() => {
              setResumeText('')
              setResumeFileName('')
              setResumeFileSize(0)
            }}
            aria-label="Remove resume"
          >
            <XIcon />
          </button>
        </ResumeFile>
      )}

      <ResumeWhy>
        <SparkleIcon />
        <span>
          With your resume, <b>your interviewer</b> tailors questions to your background instead of asking
          generic ones. It’s never shared beyond this interview.
        </span>
      </ResumeWhy>

      <CtaRow style={{ marginTop: 26 }}>
        <Btn
          disabled={isMandatory && !resumeText.trim()}
          onClick={() => {
            if (isMandatory && !resumeText.trim()) {
              message.warning('Please upload your resume to continue.')
              return
            }
            handleStartInterview()
          }}
        >
          Start interview <ChevronRight />
        </Btn>
        {!isMandatory && !resumeText.trim() && (
          <GhostBtn onClick={handleStartInterview}>Skip and continue</GhostBtn>
        )}
      </CtaRow>
    </Wrap>
  )

  /* ====================================================================== */
  /* Layout                                                                  */
  /* ====================================================================== */
  return (
    <Shell>
      {isTimerActive && <Timer timerPhase={timerPhase} countdown={countdown} />}

      {showFullscreenModal && (
        <Overlay>
          <ModalCard>
            <h2>Enable full-page assessment mode</h2>
            <p>
              The interview opens in full-page mode for proctoring. The screen will be
              dedicated to the conversation.
            </p>
            <ul>
              <li>The interview takes over the entire screen.</li>
              <li>Switching tabs or exiting fullscreen is not allowed (max 3 warnings).</li>
              <li>Audio &amp; video are recorded for review.</li>
            </ul>
            <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 20 }}>
              <Btn onClick={requestFullscreen}>Accept &amp; start interview</Btn>
            </div>
          </ModalCard>
        </Overlay>
      )}

      {!isTimerActive && (
        <>
          <TopBar>
            <div style={{ display: 'flex', flexDirection: 'column', lineHeight: 1.2 }}>
              <TBBrand>AI Interview</TBBrand>
              <TBRole>{[role, company !== 'Your role' ? company : null].filter(Boolean).join(' · ')}</TBRole>
            </div>
            <div style={{ flex: 1 }} />
            <button
              onClick={handleBack}
              style={{
                background: T.bgElevated,
                border: 'none',
                cursor: 'pointer',
                height: 34,
                padding: '0 12px',
                borderRadius: T.radiusMd,
                color: T.textMuted,
                boxShadow: `inset 0 0 0 1px ${T.borderDefault}`,
                fontSize: 12.5,
                fontWeight: 600,
                fontFamily: 'inherit',
              }}
            >
              Exit
            </button>
          </TopBar>

          {stage === STAGE_WELCOME && renderWelcome()}
          {stage === STAGE_READY && renderReadiness()}
          {stage === STAGE_BIOMETRIC && renderBiometric()}
          {stage === STAGE_RESUME && renderResume()}
        </>
      )}
    </Shell>
  )
}

export default AIInterviewInstructions
